import { Alert, IconButton, Toast as OmakaseToast, PlainButton } from 'omakase';
import { AlertProps } from '@material-ui/lab';
import { IconClose } from 'ui/icons';
import { Toast as ToastType } from '@tock/toast';

import { IconCrossLarge } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { toastSelector } from 'selectors/toastSelectors';
import { useActions } from '@tock/shared/utils/connect';
import { usePrevious } from '@tock/hooks';
import { useSelector } from 'react-redux';
import React from 'react';

const errorTimeout = 10000;
const infoTimeout = 6000;

function getAlertSeverityFromType(role: 'error' | 'info' | undefined): AlertProps['severity'] {
  switch (role) {
    case 'error':
      return 'error';
    case 'info':
      return 'success';
    case undefined:
      return 'info';
  }
}

/**
 * Component for showing a toast
 */
const Toast = () => {
  const toasts = useSelector(toastSelector);

  return (
    <>
      {toasts.map((t) => (
        <ToastBody key={t.id} {...t} />
      ))}
    </>
  );
};

const ToastBody: React.FC<ToastType> = (toast) => {
  const { actions } = useActions();
  const [activeTimeoutId, setActiveTimeoutId] = React.useState<NodeJS.Timeout | undefined>(
    undefined
  );

  const { type, message, linkText, onClickLink, id } = toast;

  const prevToast = usePrevious(toast);

  React.useEffect(() => {
    if (message && message !== prevToast?.message) {
      if (activeTimeoutId) {
        clearTimeout(activeTimeoutId);
        setActiveTimeoutId(undefined);
      }

      const timeoutId = setTimeout(
        () => {
          actions.toast.remove(id);
        },
        type === 'error' ? errorTimeout : infoTimeout
      );
      setActiveTimeoutId(timeoutId);
    }

    return () => {
      if (activeTimeoutId) {
        clearTimeout(activeTimeoutId);
      }
    };
  }, [activeTimeoutId, prevToast, toast, message, type, id, actions.toast]);

  const isOpen = !!message;
  const severity = getAlertSeverityFromType(type);
  const hasLink = linkText && onClickLink;

  const handleClose = () => {
    actions.toast.remove(id);
  };

  const actionComponent = hasLink ? (
    <PlainButton onClick={onClickLink} variant="primary" size="small">
      {linkText}
    </PlainButton>
  ) : (
    <IconButton aria-label="Close message" size="xsmall" variant="ghost" onClick={handleClose}>
      <IconMigrationUtil legacyIcon={<IconClose />} newIcon={<IconCrossLarge />} />
    </IconButton>
  );

  return (
    <OmakaseToast
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      open={isOpen}
      onClick={() => actions.toast.remove(id)}
      slimRight={!hasLink}
    >
      <Alert severity={severity} layout="row" action={actionComponent}>
        {message}
      </Alert>
    </OmakaseToast>
  );
};

export default Toast;
